import React from 'react';
import {
  CheckCircle, XCircle, Package, Printer, Eye, Mail, FileText, CreditCard, Edit, Trash2, MessageSquare
} from 'lucide-react';

const REPAIR_STATUSES = ['En attente de réception', 'En attente de récupération', 'Reçu', 'En Cours', 'Attente Pièces', 'Terminé', 'Non Réparable', 'Livré', 'Refusé'];

const statusColors = {
  'En attente de réception': 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  'En attente de récupération': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  'Reçu': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'En Cours': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  'Attente Pièces': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  'Terminé': 'bg-green-500/20 text-green-300 border-green-500/30',
  'Non Réparable': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'Livré': 'bg-red-500/20 text-red-300 border-red-500/30',
  'Refusé': 'bg-red-500/20 text-red-400 border-red-500/30',
};

export const RepairsTab = ({
  repairs, repairStatusFilter, setRepairStatusFilter, search,
  confirmArrival, acceptRefuseRepair, updateRepairStatus, deleteRepair,
  openPriceModal, openEditModal, handlePrintPdf, handlePrintInvoice,
  sendDocumentEmail, sendingEmailRef,
  statusModalRepair, setStatusModalRepair,
  priceModalRepair, setPriceModalRepair, priceForm, setPriceForm, savePrice,
  repairInvoices, markInvoicePaid, printInvoiceById, finalizeBilling,
  onOpenChat, onOpenDetail,
  t, API_URL
}) => {
  const pendingArrivals = repairs.filter(r => r.status === 'En attente de réception' || r.status === 'En attente de récupération');
  const repairCounts = { all: repairs.length };
  repairs.forEach(r => { repairCounts[r.status] = (repairCounts[r.status] || 0) + 1; });

  const filtered = repairs.filter(r =>
    !search || r.ref?.toLowerCase().includes(search.toLowerCase()) || r.company_name?.toLowerCase().includes(search.toLowerCase()) || r.brand?.toLowerCase().includes(search.toLowerCase()) || r.model?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Pending arrivals */}
      {pendingArrivals.length > 0 && (
        <div className="glass p-4 border border-orange-500/30" data-testid="b2b-pending-arrivals">
          <h3 className="text-lg font-bold text-orange-400 mb-3 flex items-center gap-2">
            <Package className="w-5 h-5" /> En attente de réception ({pendingArrivals.length})
          </h3>
          <div className="space-y-2">
            {pendingArrivals.map(r => (
              <div key={r.ref} className="bg-white/5 rounded-xl p-3 border border-orange-500/20 flex items-center justify-between gap-4" data-testid={`pending-arrival-${r.ref}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-orange-400 font-bold font-mono">{r.ref}</span>
                    <span className="text-white text-sm">{r.brand} {r.model}</span>
                    {r.tracking_number && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                        Suivi: {r.tracking_number}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">{r.company_name} - {new Date(r.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <button onClick={() => confirmArrival(r.ref)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-bold transition-colors whitespace-nowrap flex items-center gap-2"
                  data-testid={`confirm-b2b-arrival-${r.ref}`}>
                  <CheckCircle className="w-4 h-4" /> Confirmer réception
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: '', label: `Toutes (${repairCounts.all})` },
          ...REPAIR_STATUSES.map(s => ({ key: s, label: `${s === 'Attente Pièces' ? 'Att. Pièces' : s} (${repairCounts[s] || 0})` }))
        ].map(tab => (
          <button key={tab.key} onClick={() => setRepairStatusFilter(tab.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${repairStatusFilter === tab.key ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
            data-testid={`b2b-repair-filter-${tab.key || 'all'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Repairs Table */}
      <div className="glass overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] table-dark-custom text-sm">
            <thead>
              <tr>
                <th>{t('adminB2b.ref')}</th>
                <th>{t('adminB2b.company')}</th>
                <th>{t('adminB2b.brandModel')}</th>
                <th>{t('adminB2b.problem')}</th>
                <th>{t('adminB2b.delivery')}</th>
                <th>{t('adminB2b.price')}</th>
                <th>{t('adminB2b.status')}</th>
                <th>{t('adminB2b.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-gray-400">{t('adminB2b.noRepairs')}</td></tr>
              ) : filtered.map(repair => (
                <tr key={repair.ref}>
                  <td className="font-bold text-purple-400 font-mono">{repair.ref}</td>
                  <td className="text-white">{repair.company_name}</td>
                  <td>
                    <div className="font-semibold text-white">{repair.brand} {repair.model}</div>
                    {repair.imei_serial && <div className="text-xs text-gray-500 font-mono">{repair.imei_serial}</div>}
                  </td>
                  <td className="text-gray-300 max-w-[150px] truncate">{repair.problem || '-'}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${repair.delivery_method === 'postal' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : repair.delivery_method === 'recuperation' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/10 text-gray-300 border border-white/10'}`}>
                      {repair.delivery_method === 'postal' ? 'Postal' : repair.delivery_method === 'recuperation' ? 'Récup.' : 'Boutique'}
                    </span>
                    {repair.tracking_number && <div className="text-xs text-cyan-400 mt-0.5 font-mono">{repair.tracking_number}</div>}
                    {repair.pickup_address && <div className="text-xs text-amber-400 mt-0.5 truncate max-w-[120px]" title={repair.pickup_address}>{repair.pickup_address}</div>}
                  </td>
                  <td>
                    <button onClick={() => openPriceModal(repair)} className="font-bold text-white hover:text-purple-300 transition-colors cursor-pointer underline decoration-dotted" title="Modifier le prix" data-testid={`b2b-price-btn-${repair.ref}`}>
                      {((repair.price_repair || 0) + (repair.price_diagnostic || 0)).toFixed(2)} &euro;
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => {
                        if (repair.status === 'En attente de réception') { confirmArrival(repair.ref); }
                        else { setStatusModalRepair(repair); }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all hover:brightness-125 cursor-pointer ${statusColors[repair.status] || 'bg-gray-500/20 text-gray-300 border-gray-500/30'}`}
                      data-testid={`b2b-status-btn-${repair.ref}`}>
                      {repair.status === 'En attente de réception' ? 'Att. réception' : repair.status}
                    </button>
                  </td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      {repair.staff_accepted === null && (
                        <>
                          <button onClick={() => acceptRefuseRepair(repair.ref, 'accept')} className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30" title="Accepter" data-testid={`b2b-accept-${repair.ref}`}><CheckCircle className="w-4 h-4" /></button>
                          <button onClick={() => acceptRefuseRepair(repair.ref, 'refuse')} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30" title="Refuser" data-testid={`b2b-refuse-${repair.ref}`}><XCircle className="w-4 h-4" /></button>
                        </>
                      )}
                      {repair.staff_accepted === true && <span className="p-2 text-green-400" title="Acceptée"><CheckCircle className="w-4 h-4" /></span>}
                      {repair.staff_accepted === false && <span className="p-2 text-red-400" title="Refusée"><XCircle className="w-4 h-4" /></span>}
                      <button onClick={() => handlePrintPdf(repair.ref)} className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" title="Imprimer fiche dépôt" data-testid={`b2b-print-${repair.ref}`}><Printer className="w-4 h-4" /></button>
                      <button onClick={() => sendDocumentEmail(repair.ref, 'fiche')} disabled={sendingEmailRef === repair.ref} className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 disabled:opacity-50" title="Envoyer fiche par email" data-testid={`b2b-email-fiche-${repair.ref}`}><Mail className="w-4 h-4" /></button>
                      {((repair.price_repair || 0) + (repair.price_diagnostic || 0)) > 0 && !repair.invoice_cancelled && (
                        <>
                          <button onClick={() => handlePrintInvoice(repair.ref)} className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" title="Imprimer facture" data-testid={`b2b-print-invoice-${repair.ref}`}><FileText className="w-4 h-4" /></button>
                          <button onClick={() => sendDocumentEmail(repair.ref, 'facture')} disabled={sendingEmailRef === repair.ref} className="p-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 disabled:opacity-50" title="Envoyer facture par email" data-testid={`b2b-email-facture-${repair.ref}`}><Mail className="w-4 h-4" /></button>
                        </>
                      )}
                      {repair.photos?.length > 0 && (
                        <a href={`${API_URL}${repair.photos[0]}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" title={`${repair.photos.length} photo(s)`}><Eye className="w-4 h-4" /></a>
                      )}
                      <button onClick={() => openEditModal(repair)} className="p-2 rounded-lg bg-violet-500/20 text-violet-400 hover:bg-violet-500/30" title="Modifier la réparation" data-testid={`b2b-edit-${repair.ref}`}><Edit className="w-4 h-4" /></button>
                      {onOpenDetail && <button onClick={() => onOpenDetail(repair)} className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" title="Fiche detail" data-testid={`b2b-detail-${repair.ref}`}><FileText className="w-4 h-4" /></button>}
                      {onOpenChat && <button onClick={() => onOpenChat(repair)} className="p-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30" title="Chat" data-testid={`b2b-chat-${repair.ref}`}><MessageSquare className="w-4 h-4" /></button>}
                      <button onClick={() => deleteRepair(repair.ref)} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30" title="Supprimer" data-testid={`b2b-delete-${repair.ref}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {repair.back_glass_broken && <div className="text-xs text-red-400 mt-1">Vitre arr. cassée</div>}
                    {repair.customer_provides_part && <div className="text-xs text-blue-400 mt-1">Pièce client</div>}
                    {repair.confirmation_validated && <div className="text-xs text-purple-400 mt-1">BC validé</div>}
                    {repair.invoice_cancelled && <div className="text-xs text-red-400 mt-1 font-bold">Facture annulée</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Change Modal */}
      {statusModalRepair && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setStatusModalRepair(null)}>
          <div className="bg-gray-900 border border-white/10 rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold mb-1">{t('adminB2b.changeStatus')}</h3>
            <p className="text-sm text-gray-400 mb-4">{statusModalRepair.ref} - {statusModalRepair.brand} {statusModalRepair.model}</p>
            <div className="space-y-2">
              {REPAIR_STATUSES.filter(s => s !== statusModalRepair.status && s !== 'En attente de réception' && s !== 'En attente de récupération').map(s => (
                <button key={s} onClick={() => updateRepairStatus(statusModalRepair.ref, s)}
                  className={`w-full px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors text-left ${statusColors[s]} hover:brightness-125`}
                  data-testid={`b2b-modal-status-${s}`}>
                  {s}
                  {s === 'Refusé' && <span className="block text-xs font-normal mt-0.5 opacity-75">La facture sera automatiquement annulée</span>}
                </button>
              ))}
            </div>
            <button onClick={() => setStatusModalRepair(null)} className="w-full mt-3 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 text-sm hover:bg-white/10 transition-colors">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Price Modal */}
      {priceModalRepair && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setPriceModalRepair(null)}>
          <div className="bg-gray-900 border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold mb-1">{t('adminB2b.setPrice')}</h3>
            <p className="text-sm text-gray-400 mb-4">{priceModalRepair.ref} - {priceModalRepair.brand} {priceModalRepair.model}</p>
            {(() => {
              const diagInv = repairInvoices.find(i => i.invoice_type === 'diagnostic' && !i.cancelled);
              const diagLocked = diagInv && diagInv.payment_status === 'paid';
              return (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('adminB2b.repairPrice')} (&euro;)</label>
                    <input type="number" step="0.01" min="0" value={priceForm.price_repair}
                      onChange={e => setPriceForm({...priceForm, price_repair: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none"
                      data-testid="price-modal-repair" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      {t('adminB2b.diagPrice')} (&euro;)
                      {diagLocked && <span className="ml-2 text-green-400 text-xs font-bold">{t('adminB2b.locked')}</span>}
                    </label>
                    <input type="number" step="0.01" min="0" value={priceForm.price_diagnostic}
                      onChange={e => setPriceForm({...priceForm, price_diagnostic: parseFloat(e.target.value) || 0})}
                      disabled={diagLocked}
                      className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none ${diagLocked ? 'bg-green-500/10 border-green-500/30 text-green-300 cursor-not-allowed' : 'bg-white/5 border-white/10 text-white focus:border-purple-500/50'}`}
                      data-testid="price-modal-diagnostic" />
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <span className="text-gray-400 text-xs">{t('adminB2b.total')}: </span>
                    <span className="text-white font-bold">{(priceForm.price_repair + priceForm.price_diagnostic).toFixed(2)} &euro;</span>
                  </div>
                  {repairInvoices.filter(i => !i.cancelled).length > 0 && (
                    <div className="space-y-2 pt-2 border-t border-white/10">
                      <p className="text-xs text-gray-400 font-semibold">{t('adminB2b.existingInvoices')} :</p>
                      {repairInvoices.filter(i => !i.cancelled).map(inv => (
                        <div key={inv.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 text-xs">
                          <div>
                            <span className="font-mono text-white">{inv.invoice_number}</span>
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-bold ${
                              inv.invoice_type === 'diagnostic' ? 'bg-cyan-500/20 text-cyan-400' :
                              inv.invoice_type === 'repair' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-orange-500/20 text-orange-400'
                            }`}>
                              {inv.invoice_type === 'diagnostic' ? 'Diagnostic' : inv.invoice_type === 'repair' ? 'Réparation' : 'Combinée'}
                            </span>
                            <span className="ml-1 text-gray-400">{parseFloat(inv.total || 0).toFixed(2)}&euro;</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {inv.payment_status === 'paid' ? (
                              <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 font-bold">Payée ({inv.payment_method})</span>
                            ) : (
                              <div className="flex items-center gap-1">
                                <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-bold">En attente</span>
                                <button onClick={() => {
                                  const method = prompt('Mode de paiement ?\n- Espèces\n- CB\n- Virement\n- Chèque\n- Stripe', 'Espèces');
                                  if (method) markInvoicePaid(inv.id, method);
                                }}
                                  className="px-2 py-1 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 font-bold"
                                  data-testid={`mark-paid-${inv.id}`}>
                                  <CreditCard className="w-3 h-3 inline mr-0.5" />Payé
                                </button>
                              </div>
                            )}
                            <button onClick={() => printInvoiceById(inv.id)}
                              className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                              data-testid={`print-invoice-${inv.id}`}>
                              <Printer className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setPriceModalRepair(null)} className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 text-sm hover:bg-white/10 transition-colors">
                {t('common.cancel')}
              </button>
              <button onClick={savePrice} className="flex-1 px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition-colors" data-testid="price-modal-save">
                {t('common.save')}
              </button>
            </div>
            {(priceModalRepair.status === 'Terminé' || priceModalRepair.status === 'Livré') && repairInvoices.some(i => !i.cancelled && !i.finalized) && (
              <button onClick={() => finalizeBilling(priceModalRepair.ref)}
                className="w-full mt-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                data-testid="finalize-billing-btn">
                <CheckCircle className="w-4 h-4" /> {t('adminB2b.finalize')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
